import { useMemo, useState } from "react";
import type { DateRange } from "../components/DateSelector";
import { addDays, fromBusinessDate } from "../utils/date";

/**
 * Диапазон дат календаря шапки: у каждой вкладки свой, пока сам не выбран —
 * последние `days` дней от текущего бизнес-дня.
 *
 * `eff` — business UTC-midnight (в этом виде даты уходят в API через fmtDateApi),
 * `local` — то же в местной зоне, каким его ждёт DateSelector.
 */
export function useHeaderRange(date: Date, days: number, defaultOf?: (d: Date) => DateRange): {
  eff: DateRange; local: DateRange; set: (r: DateRange) => void;
} {
  const [range, setRange] = useState<DateRange>({ start: null, end: null });
  const fallback = useMemo<DateRange>(
    () => (defaultOf ? defaultOf(date) : { start: addDays(date, -(days - 1)), end: date }),
    [date, days, defaultOf],
  );
  const eff = range.start && range.end ? range : fallback;
  const local = useMemo<DateRange>(
    () => ({
      start: eff.start ? fromBusinessDate(eff.start) : null,
      end: eff.end ? fromBusinessDate(eff.end) : null,
    }),
    [eff.start, eff.end],
  );
  return { eff, local, set: setRange };
}

/**
 * 21.09 (владелец): «Мир Кв» разбирают на следующий день, поэтому по умолчанию — ВЧЕРА,
 * а в понедельник ещё и позавчера (за выходные). Дальше период правится календарём шапки.
 * Ссылка на функцию стабильна — иначе fallback пересчитывался бы каждый рендер.
 */
export function mirKvDefaultRange(date: Date): DateRange {
  const monday = date.getUTCDay() === 1;   // бизнес-дата хранится как UTC-полночь
  return { start: addDays(date, monday ? -2 : -1), end: addDays(date, -1) };
}
